/**
 * Displays a countdown timer. It takes in a number of seconds left and updates the display every second/minute/hour until the timer reaches zero. It also triggers a custom event when the timer finishes.
 */

// Import dependencies
import { unsafeCSS, css, html, LitElement } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { isEmpty, isDefined } from '@automator/utilities';
import dayjs from 'dayjs';

// eslint-disable-next-line require-jsdoc
@customElement( 'uo-timer' )
export class AutomatorTimer extends LitElement {
	// Properties
	// The number of seconds left in the timer.
	@property( { type: Number, attribute: 'seconds-left' } ) accessor secondsLeft = 0;

	// Non-reactive properties
	// A boolean that tracks whether the secondsLeft property was positive or negative in the previous update. Used to trigger the finish event.
	wasPositive = true;

	// The date and time when the timer will finish. It is created from the current time and the secondsLeft property in the render() method.
	timerDate = null;

	// The ID of the timeout used to automatically update the timer display. It is set up and cleared in the handleRefreshAutomatically() method.
	_autoRefreshTimeout = null;

	/**
	 * Renders the timer display using the html template. If the timerDate is not defined, it creates it using the current time and the secondsLeft property.
	 *
	 * @return {TemplateResult}
	 */
	render() {
		// If the timeDate is not defined yet, create it
		if ( ! isDefined( this.timerDate ) ) {
			this.timerDate = dayjs().add( this.secondsLeft, 'second' );
		}

		return html`${ _uo.utility.getRelativeTime( 'date', this.timerDate ) }`;
	}

	/**
	 * Removes the old timeout and timerDate if the secondsLeft property changes.
	 *
	 * @param {Map} changedProperties The properties that changed
	 */
	willUpdate( changedProperties ) {
		// Check if the date to the next process changed
		if ( changedProperties.has( 'secondsLeft' ) ) {
			// Updating seconds left destroys the old date
			this.timerDate = null;

			// Clear the timeout
			if ( isDefined( this._autoRefreshTimeout ) ) {
				clearTimeout( this._autoRefreshTimeout );
			}
		}
	}

	/**
	 * Triggers the finish event and sets up the next automatic update if the secondsLeft property changes.
	 *
	 * @param {Map} changedProperties The properties that changed
	 */
	updated( changedProperties ) {
		// Check if the date to the next process changed
		if ( changedProperties.has( 'secondsLeft' ) ) {
			// Check whether we should trigger the finish event or now
			this.handleFinishEvent();

			// Handle the next automatic update
			this.handleRefreshAutomatically();
		}
	}

	/**
	 * Checks if the timer has finished and triggers the custom event if it has.
	 */
	handleFinishEvent() {
		// Check if secondsLeft turned negative
		if ( this.secondsLeft <= 0 && this.wasPositive ) {
			// Trigger your event here
			this.dispatchEvent(
				new CustomEvent( 'uo-timer-finished' )
			);

			// Set wasPositive to false
			this.wasPositive = false;
		} else if ( this.secondsLeft > 0 ) {
			// If secondsLeft turned positive again, reset wasPositive to true
			this.wasPositive = true;
		}
	}

	/**
	 *  Sets up a timeout to update the timer display automatically.
	 */
	handleRefreshAutomatically() {
		// If it was defined, remove the old timeout
		if ( ! isEmpty( this._autoRefreshTimeout ) ) {
			clearTimeout( this._autoRefreshTimeout );
		}

		// Sets the timeout for the update
		this._autoRefreshTimeout = setTimeout( () => {
			// Calculate the number of seconds left and refresh the update.
			// The update will create another timeout changes to `.willUpdate`, so no need to handle that.
			this.secondsLeft = this.timerDate.diff( dayjs(), 'second' );
		}, this.nextUpdate * 1000 );
	}

	/**
	 * Remove the timout if it exists
	 */
	disconnectedCallback() {
		super.disconnectedCallback();

		if ( isDefined( this._autoRefreshTimeout ) ) {
			clearTimeout( this._autoRefreshTimeout );
		}
	}

	/**
	 * Calculates the time until the next automatic update based on the remaining time left in the timer.
	 *
	 * @return {Number} The time until the next update, in seconds
	 */
	get nextUpdate() {
		// If less than 2 minutes, refresh every 3 second
		if ( Math.abs( this.secondsLeft ) < ( 60 * 2 ) ) {
			return 3;
		}

		// If less than 2 hours, refresh every 1 minute
		if ( Math.abs( this.secondsLeft ) < ( 60 * 60 * 2 ) ) {
			return 60;
		}

		// Otherwise, refresh every 1 hour
		return ( 60 * 60 );
	}
}
